// CARTO Hosted Apps serve the bundle under /app/<slug>/ behind the CARTO login, with a per-viewer
// carto-info.json next to it. Its token is the viewer's own CARTO user JWT, so the app runs as the
// signed-in CARTO user. entry.tsx loads it before the first render.
// https://docs.carto.com/carto-for-developers/guides/build-a-hosted-application
//
// The token feeds the USER token path (getUserAccessToken) and deliberately NOT the static one: it
// carries the `permissions` claim that role.ts decodes, while getPublicAuth() would make every
// viewer `suscriptor` (role.ts gives public mode all-access by design). Module-level state, not
// settings: auth.ts reads it unconditionally, so standard builds (where nothing ever sets it) keep
// their behaviour with no HOSTED_APP branching inside auth.ts.

import { CARTO_SIGN_OUT_URL } from '@/config/settings'

interface CartoInfo {
  accessToken?: string
  // The viewer's CARTO API host, e.g. https://gcp-us-east1.api.carto.com
  apiBaseUrl?: string
  // Unix seconds at which the token and the app session end, when known
  expiresAt?: number
  // The signed-in viewer
  user?: { email?: string | null; picture?: string | null }
}

// Who the header shows: hosted mode mounts no Auth0 provider, so useAuth0() has no user
export interface HostedViewer {
  email: string | null
  picture: string | null
}

export interface HostedAppSession {
  accessToken: string
  apiBaseUrl: string | null
  viewer: HostedViewer
}

// carto-info.json returns the same token until the session ends; the platform's /app/_session
// sends the viewer through sign-in and back with a fresh one. A renewal that lands on another dead
// session within this window stops instead of looping.
const RENEWAL_KEY = 'hosted-app-session-renewal'
const RENEWAL_GUARD_MS = 60_000
// setTimeout fires at once past this delay (2^31 - 1 ms, ~24.8 days)
const MAX_TIMEOUT_MS = 2 ** 31 - 1

let hostedUserToken: string | null = null

export function setHostedUserToken(token: string | null) {
  hostedUserToken = token
}

export function getHostedUserToken(): string | null {
  return hostedUserToken
}

let hostedViewer: HostedViewer | null = null

export function setHostedViewer(viewer: HostedViewer | null) {
  hostedViewer = viewer
}

export function getHostedViewer(): HostedViewer | null {
  return hostedViewer
}

// Resolves with the viewer's token and API host, or never while the page leaves to renew the
// session. Rejects when carto-info.json fails, or the renewal just did.
export async function loadHostedAppSession(): Promise<HostedAppSession> {
  // Absolute: a relative path would resolve against a deep link's route
  const response = await fetch(`${import.meta.env.BASE_URL}carto-info.json`, {
    credentials: 'include',
    // Per viewer and short-lived: never cache it
    cache: 'no-store',
  })
  if (response.status === 401) {
    return renewSession()
  }
  if (!response.ok) {
    throw new Error(`carto-info.json: HTTP ${response.status}`)
  }

  const { accessToken, apiBaseUrl, expiresAt, user } =
    (await response.json()) as CartoInfo
  // Only a finite number of seconds schedules anything: any other shape would turn into a NaN
  // delay, which setTimeout fires at once, and the page would bounce through /app/_session.
  const msLeft = Number.isFinite(expiresAt)
    ? expiresAt! * 1000 - Date.now()
    : Infinity
  if (!accessToken || msLeft <= 0) {
    return renewSession()
  }
  if (msLeft !== Infinity) {
    // Every CARTO call 401s once the token expires: renew at that moment rather than let the
    // viewer meet the errors.
    setTimeout(renewHostedSession, Math.min(msLeft, MAX_TIMEOUT_MS))
  }
  return {
    accessToken,
    apiBaseUrl: apiBaseUrl ?? null,
    viewer: { email: user?.email ?? null, picture: user?.picture ?? null },
  }
}

// The platform documents no sign-out for hosted apps. This ends both sessions the viewer holds:
// the app's own cookie, which would otherwise keep the app open in this browser for up to an hour,
// and the CARTO one, through the page the Workspace's own "Log out" ends on. The viewer lands on
// the CARTO login.
export async function signOutOfHostedApp(): Promise<void> {
  try {
    await fetch('/app/_session', { method: 'DELETE', credentials: 'include' })
  } catch {
    // The CARTO sign-out still goes ahead; the cookie expires within the hour
  }
  window.location.assign(CARTO_SIGN_OUT_URL)
}

// Idempotent, like installGlobalErrorHandlers: HMR re-runs entry.tsx and must not stack listeners.
let chunkRenewalInstalled = false

// The platform serves the bundle behind a session cookie that lasts less than the token (1 h vs
// 24 h, measured), and it only serves the active version's files. Past the cookie's end, or after
// a deploy or rollback while the tab is open, a lazily loaded route fails with "Failed to fetch
// dynamically imported module". Vite reports that as `vite:preloadError`: going through
// /app/_session renews the cookie and reloads the active version. The event is only observed, not
// prevented: preventing it makes the import resolve to undefined and React Router reports a
// misleading TypeError instead of the real failure (measured on a live deploy). The loop guard
// leaves the error to the error boundary when a renewal just happened.
export function renewSessionOnChunkLoadFailure(): void {
  if (chunkRenewalInstalled) return
  chunkRenewalInstalled = true
  window.addEventListener('vite:preloadError', () => {
    if (mayRenewAutomatically()) redirectToSession()
  })
}

// Sign-in again with no loop guard, for the viewer's request (the "retry" of the boot error
// screen) and the token's scheduled end: neither can loop, a click or a timer fires once per page.
export function renewHostedSession(): void {
  recordRenewal()
  redirectToSession()
}

function renewSession(): Promise<never> {
  if (!mayRenewAutomatically()) {
    return Promise.reject(new Error('The CARTO session could not be renewed'))
  }
  redirectToSession()
  return new Promise(() => {
    // Never settles: the page is leaving, so nothing renders meanwhile
  })
}

// An automatic renewal goes ahead only when none happened just before AND this one is recorded:
// without storage the guard could never stop a loop, so the error screen (whose retry still
// renews) takes over instead of the page bouncing through /app/_session for ever.
function mayRenewAutomatically(): boolean {
  return !renewedRecently() && recordRenewal()
}

function recordRenewal(): boolean {
  try {
    sessionStorage.setItem(RENEWAL_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}

function redirectToSession() {
  // BASE_URL is /app/<slug>/
  const slug = import.meta.env.BASE_URL.split('/').filter(Boolean).at(-1) ?? ''
  window.location.assign(`/app/_session?slug=${encodeURIComponent(slug)}`)
}

function renewedRecently(): boolean {
  return Date.now() - lastRenewal() < RENEWAL_GUARD_MS
}

function lastRenewal(): number {
  try {
    return Number(sessionStorage.getItem(RENEWAL_KEY)) || 0
  } catch {
    return 0
  }
}
